import { Fragment, useRef, useCallback, useMemo } from "react";
import useKeyboardShortcut from "shared/hooks/keyboard/useKeyboardShortcut";
import "./TwistKeyboardMoveCommander.css";
import keyboardLayout from "shared/assets/images/keyboards/keyboard-joy.png";
import DEFAULT_JOY_CONTROL_MAPPING from "features/robot-teleop/constants/defaultJoyControlMapping";

const JoyKeyboardTopicCommander = (props) => {
  const {
    OnCommandCreated: onCommandCreated,
    EnableKeyboard: enableKeyboard,
    ThrottleInterval: throttleInterval,
    JoyControlMapping: joyControlMapping,
  } = props;

  const activeMapping = joyControlMapping ?? DEFAULT_JOY_CONTROL_MAPPING;

  const joyDataRef = useRef({
    axes: new Array(activeMapping.axes_count).fill(0),
    buttons: new Array(activeMapping.buttons_count).fill(0),
  });

  // map key to axes index and value
  const mapKeyToAxesIndexValue = useMemo(() => {
    const axisBindings = activeMapping.keyboard_bindings.filter(
      (b) => b.type === "axis"
    );
    if (axisBindings.length > 0) {
      return Object.fromEntries(axisBindings.map((b) => [b.key, [b.index, b.value]]));
    }
    // fallback hardcoded defaults
    return {
      // left stick
      KeyA: [0, -1],
      KeyD: [0, 1],
      KeyW: [1, -1],
      KeyS: [1, 1],
      // right stick
      KeyJ: [2, -1],
      KeyL: [2, 1],
      KeyI: [3, -1],
      KeyK: [3, 1],
      // LT/RT
      Digit1: [4, 1],
      Digit3: [5, 1],
    };
  }, [activeMapping]);

  // Move bindings - memoized for performance
  const mapKeyToButtonIndex = useMemo(() => {
    const buttonBindings = activeMapping.keyboard_bindings.filter(
      (b) => b.type === "button"
    );
    if (buttonBindings.length > 0) {
      return Object.fromEntries(buttonBindings.map((b) => [b.key, [b.index, b.value]]));
    }
    // fallback hardcoded defaults
    return {
      Space: [0, 1], // Button A
      KeyE: [1, 1], // Button B
      KeyQ: [2, 1], // Button X
      KeyR: [3, 1], // Button Y
      KeyZ: [4, 1], // Button LB
      KeyC: [5, 1], // Button RB
      Digit1: [6, 1], // Button L2
      Digit3: [7, 1], // Button R2
      Digit6: [8, 1], // Button Select
      Digit7: [9, 1], // Button Start
      Digit9: [10, 1], // Button L3
      Digit0: [11, 1], // Button R3
      ArrowUp: [12, 1],
      ArrowDown: [13, 1],
      ArrowLeft: [14, 1],
      ArrowRight: [15, 1],
    };
  }, [activeMapping]);

  const controlKeys = useMemo(
    () => [
      ...Object.keys(mapKeyToButtonIndex),
      ...Object.keys(mapKeyToAxesIndexValue),
    ],
    [mapKeyToButtonIndex, mapKeyToAxesIndexValue]
  );

  const createJoyData = useCallback(
    (pressedKeyCodes) => {
      const joyData = {
        axes: new Array(activeMapping.axes_count).fill(0),
        buttons: new Array(activeMapping.buttons_count).fill(0),
      };
      pressedKeyCodes.forEach((key) => {
        if (mapKeyToButtonIndex[key] !== undefined) {
          const [index, value] = mapKeyToButtonIndex[key];
          joyData.buttons[index] = value;
        }

        if (mapKeyToAxesIndexValue[key] !== undefined) {
          const [index, value] = mapKeyToAxesIndexValue[key];
          joyData.axes[index] = value;
        }
      });
      return joyData;
    },
    [mapKeyToButtonIndex, mapKeyToAxesIndexValue, activeMapping]
  );

  // Handle keyboard input using useKeyboardShortcut hook
  const handleKeyboardInput = useCallback(
    (event, { key, keyCode, pressedKeys, pressedKeyCodes }) => {
      console.log("Key pressed:", key, "All pressed keys:", pressedKeys);
      // Call the command handler with the updated data
      if (onCommandCreated) {
        const joyTopicValue = createJoyData(pressedKeyCodes);
        joyDataRef.current = joyTopicValue;
        onCommandCreated(joyTopicValue, true);
      }
    },
    [onCommandCreated, createJoyData]
  );

  // Use the keyboard shortcut hook
  const { pressedKeys, pressedKeyCodes } = useKeyboardShortcut(
    enableKeyboard ? handleKeyboardInput : null,
    controlKeys,
    null,
    {
      enableKeyPress: enableKeyboard,
      trackPressedKeys: true,
      preventDefault: true,
      enableRepeat: true,
      throttleInterval: throttleInterval || 80,
    }
  );

  return (
    <Fragment>
      <div className="robot-control" tabIndex={0}>
        <img
          src={keyboardLayout}
          alt="Keyboard Layout"
          className="keyboard-layout-img"
          style={{
            width: "100%",
            maxWidth: 600,
            display: "block",
            margin: "0 auto",
          }}
        />
        {/* <h4>Mapping from keyboard to Joy</h4>
        <div>Axes: [{Object.keys(mapKeyToAxesIndexValue).join(", ")}]</div>
        <div>Buttons: [{Object.keys(mapKeyToButtonIndex).join(", ")}]</div>

        <div style={{ marginTop: "10px", fontSize: "0.9em", color: "#666" }}>
          <div>
            Axes: [{joyDataRef.current.axes.map((v) => v.toFixed(2)).join(", ")}
            ]
          </div>
          <div> Buttons: [{joyDataRef.current.buttons.join(", ")}]</div>
        </div>
        <div style={{ marginTop: "5px", fontSize: "0.8em", color: "#999" }}>
          Pressed keys: {pressedKeys.join(", ") || "None"}
        </div> */}
      </div>
    </Fragment>
  );
};

export default JoyKeyboardTopicCommander;
